import { ResearchPlotData } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { L2Target, L2Targets } from '@resistapp/common/assays';
import { EnvironmentSubType, EnvironmentType, friendlyEnvSubTypeOrType } from '@resistapp/common/environment-types';
import { friendlyL2Target } from '@resistapp/common/friendly';
import { NormalisationMode } from '@resistapp/common/types';
import { Line } from '@visx/shape';
import { useResearchContext } from '../../contexts/research-context';

interface ReferenceLineProps {
  normalizationMode?: NormalisationMode;
  yScale: (n: number) => number;
  width: number;
  left: number;
  stroke?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  opacity?: number;
}

/**
 * Renders a reference line on a chart
 * For the research view, this represents a global reference level
 */
export function BoxPlotMeanLine({
  yScale,
  width,
  left,
  stroke = '#666666',
  strokeWidth = 1.5,
  strokeDasharray = '5,5',
  opacity = 0.7,
}: ReferenceLineProps) {
  const { getReferenceLevel } = useResearchContext();
  const [referenceValue] = getReferenceLevel();

  if (referenceValue === null) {
    return null;
  }

  const y = yScale(Math.log10(referenceValue));

  return (
    <Line
      className="download_reference-line"
      from={{ x: left, y }}
      to={{ x: left + width, y }}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeDasharray={strokeDasharray}
      opacity={opacity}
    />
  );
}

type ReferenceLevelTooltipContentProps = {
  plotData: ResearchPlotData | null;
  selectedTargets: L2Target[];
  environmentTypeOrSubtype: EnvironmentType | EnvironmentSubType | null;
};

/**
 * Generates the tooltip content for the reference level line
 */
export function ReferenceLevelTooltipContent(props: ReferenceLevelTooltipContentProps): JSX.Element | null {
  const { plotData, selectedTargets, environmentTypeOrSubtype } = props;
  if (!plotData) return null;

  const type = (environmentTypeOrSubtype && friendlyEnvSubTypeOrType(environmentTypeOrSubtype).toLowerCase()) || '';
  const { normalisationMode } = plotData;
  const metric =
    normalisationMode === NormalisationMode.LITRE
      ? 'copy number per L'
      : normalisationMode === NormalisationMode.TEN_UL_DILUTED_DNA
        ? 'copies in 10 uL diluted DNA'
        : 'relative abundance';

  const targetsPhrase =
    selectedTargets.length === 1
      ? `${selectedTargets[0].length > 4 ? friendlyL2Target(selectedTargets[0] as L2Targets).toLowerCase() : friendlyL2Target(selectedTargets[0] as L2Targets)} genes`
      : 'gene classes';

  return (
    <>
      <p style={{ marginBottom: '1rem' }}>
        <b>Mean {metric}</b> for the <b>selected {targetsPhrase}</b> in {type} across the whole Resistomap database.
      </p>
    </>
  );
}
